<template>
  <div class="bu-page">
    <div
      v-if="!isAdmin"
      class="bu-denied"
    >
      <feather-icon
        icon="ShieldOffIcon"
        size="48"
      />
      <h4>Доступ запрещён</h4>
      <p>Только администратор может регистрировать пользователей.</p>
    </div>
    <div
      v-else
      class="bu-container"
    >
      <!-- Header -->
      <div class="bu-header">
        <h4>Регистрация пользователей</h4>
        <div class="bu-domain-select">
          <label>Домен:</label>
          <select
            v-model="selectedDomainSid"
            class="bu-domain-picker"
            @change="switchDomain"
          >
            <option
              v-for="d in domains"
              :key="d.sid"
              :value="d.sid"
            >
              {{ d.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Settings: roles -->
      <div
        v-if="availableRoles.length > 0"
        class="bu-roles-bar"
      >
        <span class="bu-roles-label">Роли:</span>
        <label
          v-for="r in availableRoles"
          :key="r.sid"
          class="bu-role-check"
          :title="r.sid"
        >
          <input
            type="checkbox"
            :checked="defaultRoleSids.includes(r.sid)"
            @change="toggleRoleSid(r.sid)"
          >
          {{ r.name }}
        </label>
        <span
          v-if="defaultRoleSids.length === 0"
          class="bu-roles-warn"
        >⚠ не выбрана ни одна роль</span>
        <span
          v-if="subdivisions.length || positions.length"
          class="bu-catalog-info"
        >
          | Подразд: {{ subdivisions.length }}, Должн: {{ positions.length }}
        </span>
      </div>

      <!-- Chat-style log -->
      <div
        ref="chatLog"
        class="bu-chat"
      >
        <div class="bu-msg bu-msg--bot">
          Загрузите файл (CSV или XLSX) со списком пользователей.<br>
          Формат: <code>ФИО | Должность | Подразделение | email</code><br>
          Логин = email до @, пароль генерируется автоматически.
        </div>

        <div
          v-for="(msg, mi) in messages"
          :key="mi"
          :class="'bu-msg bu-msg--' + msg.type"
        >
          <div
            v-if="msg.type === 'file'"
            class="bu-msg-file"
          >
            <feather-icon
              icon="FileTextIcon"
              size="14"
            /> {{ msg.text }}
          </div>
          <div
            v-else-if="msg.type === 'table'"
            class="bu-msg-table-wrap"
          >
            <div class="bu-msg-text">
              {{ msg.text }}
            </div>
            <table class="bu-preview-table">
              <thead><tr><th>#</th><th>ФИО</th><th>Email</th><th>Логин</th><th>Подразделение</th></tr></thead>
              <tbody>
                <tr
                  v-for="(u, ui) in msg.users"
                  :key="ui"
                >
                  <td>{{ ui + 1 }}</td>
                  <td>{{ u.name }}</td>
                  <td>{{ u.email }}</td>
                  <td>{{ u.login }}</td>
                  <td>{{ u.subdivision }}</td>
                </tr>
              </tbody>
            </table>
            <div class="bu-msg-actions">
              <b-button
                v-if="!msg.started"
                size="sm"
                variant="primary"
                @click="startRegistration(mi)"
              >
                <feather-icon
                  icon="PlayIcon"
                  size="12"
                  class="mr-25"
                /> Зарегистрировать {{ msg.users.length }} пользователей
              </b-button>
            </div>
          </div>
          <div
            v-else-if="msg.type === 'progress'"
            class="bu-msg-progress"
          >
            <div class="bu-progress-bar">
              <div
                class="bu-progress-fill"
                :style="{ width: msg.percent + '%' }"
              />
            </div>
            <span>{{ msg.text }}</span>
          </div>
          <div v-else-if="msg.type === 'result'">
            <div
              class="bu-msg-text"
              v-html="safeHtml(msg.text)"
            />
            <div
              v-if="msg.downloadReady"
              class="bu-msg-actions"
            >
              <b-button
                size="sm"
                variant="outline-primary"
                @click="downloadWord(msg.userData)"
              >
                <feather-icon
                  icon="DownloadIcon"
                  size="12"
                  class="mr-25"
                /> Скачать (Word)
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="downloadCsv(msg.userData)"
              >
                <feather-icon
                  icon="DownloadIcon"
                  size="12"
                  class="mr-25"
                /> Скачать (CSV)
              </b-button>
            </div>
          </div>
          <div
            v-else
            class="bu-msg-text"
            v-html="safeHtml(msg.text)"
          />
        </div>
      </div>

      <!-- Editable table -->
      <div
        v-if="users.length > 0"
        class="bu-table-section"
      >
        <div class="bu-table-header">
          <strong>Пользователи ({{ users.length }})</strong>
          <div class="bu-table-tools">
            <b-button
              size="sm"
              variant="primary"
              :disabled="running || users.length === 0"
              @click="registerAll"
            >
              <b-spinner
                v-if="running"
                small
                class="mr-25"
              />
              <feather-icon
                v-else
                icon="PlayIcon"
                size="12"
                class="mr-25"
              />
              Зарегистрировать
            </b-button>
            <b-button
              v-if="doneCount > 0"
              size="sm"
              variant="outline-primary"
              @click="downloadWord(users)"
            >
              <feather-icon
                icon="DownloadIcon"
                size="12"
              /> Word
            </b-button>
            <b-button
              v-if="doneCount > 0"
              size="sm"
              variant="outline-secondary"
              @click="downloadCsv(users)"
            >
              <feather-icon
                icon="DownloadIcon"
                size="12"
              /> CSV
            </b-button>
          </div>
        </div>
        <div class="bu-table-wrap">
          <table class="bu-table">
            <thead>
              <tr><th>#</th><th>ФИО</th><th>Email</th><th>Логин</th><th>Пароль</th><th>Должность</th><th>Подразделение</th><th>Статус</th><th /></tr>
            </thead>
            <tbody>
              <tr
                v-for="(u, i) in users"
                :key="i"
                :class="'bu-tr--' + (u.status || 'pending')"
              >
                <td class="bu-num">
                  {{ i + 1 }}
                </td>
                <td><input
                  v-model="u.name"
                  class="bu-cell-input"
                  :disabled="u.status==='ok'"
                ></td>
                <td><input
                  v-model="u.email"
                  class="bu-cell-input"
                  :disabled="u.status==='ok'"
                ></td>
                <td class="bu-mono">
                  {{ u.login }}
                </td>
                <td class="bu-mono">
                  {{ u.password }}
                </td>
                <td :class="u.position ? (matchPosition(u.position) ? 'bu-match-ok' : 'bu-match-no') : ''">
                  <div class="bu-ref-cell">
                    <input
                      v-model="u.position"
                      class="bu-cell-input"
                      :disabled="u.status==='ok'"
                      @focus="refOpen = i + '-pos'"
                      @blur="refOpen = null"
                      @input="refOpen = i + '-pos'"
                    >
                    <div
                      v-if="refOpen === i + '-pos'"
                      class="bu-ref-drop"
                      @mousedown.prevent
                    >
                      <div
                        v-for="opt in filteredList(positions, u.position)"
                        :key="opt"
                        class="bu-ref-opt"
                        @click="pickRef(u, 'position', opt)"
                      >{{ opt }}</div>
                      <div
                        v-if="u.position && u.position.trim() && !exactMatch(positions, u.position)"
                        class="bu-ref-opt bu-ref-create"
                        @click="createRef(u, 'position', u.position)"
                      >+ Создать «{{ u.position }}»</div>
                    </div>
                  </div>
                </td>
                <td :class="u.subdivision ? (matchSubdivision(u.subdivision) ? 'bu-match-ok' : 'bu-match-no') : ''">
                  <div class="bu-ref-cell">
                    <input
                      v-model="u.subdivision"
                      class="bu-cell-input"
                      :disabled="u.status==='ok'"
                      @focus="refOpen = i + '-sub'"
                      @blur="refOpen = null"
                      @input="refOpen = i + '-sub'"
                    >
                    <div
                      v-if="refOpen === i + '-sub'"
                      class="bu-ref-drop"
                      @mousedown.prevent
                    >
                      <div
                        v-for="opt in filteredList(subdivisions, u.subdivision)"
                        :key="opt"
                        class="bu-ref-opt"
                        @click="pickRef(u, 'subdivision', opt)"
                      >{{ opt }}</div>
                      <div
                        v-if="u.subdivision && u.subdivision.trim() && !exactMatch(subdivisions, u.subdivision)"
                        class="bu-ref-opt bu-ref-create"
                        @click="createRef(u, 'subdivision', u.subdivision)"
                      >+ Создать «{{ u.subdivision }}»</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span
                    v-if="u.status==='ok'"
                    class="bu-st-ok"
                  >✓</span>
                  <span
                    v-else-if="u.status==='exists'"
                    class="bu-st-exists"
                  >⚠ {{ u.error }}</span>
                  <span
                    v-else-if="u.status==='error'"
                    class="bu-st-err"
                  >✗ {{ u.error }}</span>
                  <span
                    v-else
                    class="bu-st-wait"
                  >—</span>
                </td>
                <td><span
                  v-if="u.status!=='ok'"
                  class="bu-row-del"
                  @click="users.splice(i,1)"
                >✕</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Drop zone + file input -->
      <div
        class="bu-input-area"
        :class="{ 'bu-input-area--drag': dragover }"
        @dragover.prevent="dragover = true"
        @dragleave="dragover = false"
        @drop.prevent="onDrop"
      >
        <label class="bu-file-btn">
          <feather-icon
            icon="PaperclipIcon"
            size="16"
          />
          <input
            type="file"
            accept=".csv,.xlsx,.xls,.txt"
            style="display:none"
            @change="onFileSelect"
          >
        </label>
        <input
          v-model="inputText"
          class="bu-text-input"
          placeholder="Или вставьте текст: ФИО;email;подразделение..."
          @keydown.enter="onTextSubmit"
        >
        <button
          class="bu-send-btn"
          :disabled="!inputText.trim()"
          @click="onTextSubmit"
        >
          <feather-icon
            icon="SendIcon"
            size="14"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import * as XLSX from 'xlsx'
import DOMPurify from 'dompurify'
import useJwt from '@/cubus/jwt/useJwt'
import useCubus from '@/cubus/services/useCubus'
import store from '@/store'

export default {
  name: 'CbsBulkUsers',
  components: { BButton, BSpinner },
  data() {
    return {
      messages: [],
      users: [],
      inputText: '',
      dragover: false,
      defaultRoleSids: [],
      availableRoles: [],
      running: false,
      domains: [],
      selectedDomainSid: '',
      subdivisions: [], // domain catalog
      positions: [], // domain catalog
      subObjId: null, // object id for creating new
      posObjId: null,
      refOpen: null,
    }
  },
  computed: {
    domainName() {
      const d = this.domains.find(d => d.sid === this.selectedDomainSid)
      return d ? d.name : '—'
    },
    doneCount() { return this.users.filter(u => u.status === 'ok').length },
    isAdmin() {
      const sess = store.getters['cubus-store/SESSION']
      if (!sess || !sess.user || !sess.user.roles) return false
      return sess.user.roles.some(r => ['admin', 'root', 'developer'].includes((r.sid || '').toLowerCase()) || r.id === 1)
    },
    subdivisionNames() { return this.subdivisions.map(s => s.toLowerCase()) },
    positionNames() { return this.positions.map(s => s.toLowerCase()) },
  },
  created() {
    this.loadDomains()
    this.loadRoles()
    this.loadCatalogs()
  },
  methods: {
    // ── Domain loading ──
    async loadDomains() {
      // Use same API as CbsDomainSelect: method 'domains'
      try {
        const token = useJwt.getCubusToken()
        const resp = await useJwt.query({ token, query: { method: 'domains', param: {} } })
        if (resp.data && resp.data.thread) {
          // Poll thread
          const domains = await this.pollDomainsThread(resp.data.thread)
          if (domains) {
            this.domains = domains.filter(d => d.sid !== 'root')
            const sess = store.getters['cubus-store/SESSION']
            if (sess && sess.domain) {
              this.selectedDomainSid = sess.domain.sid
            } else if (this.domains.length > 0) {
              this.selectedDomainSid = this.domains[0].sid
            }
          }
        }
      } catch (e) {
        console.error('loadDomains error:', e)
      }
    },

    async pollDomainsThread(threadName) {
      const token = useJwt.getCubusToken()
      for (let i = 0; i < 20; i++) {
        await new Promise(r => setTimeout(r, 500))
        const resp = await useJwt.query({ token, query: { method: 'thread', param: { threadname: threadName } } })
        const t = resp.data && resp.data.thread
        if (t && t.status === 'done' && t.result && t.result.domains) {
          return t.result.domains
        }
      }
      return null
    },

    async switchDomain() {
      if (!this.selectedDomainSid) return
      try {
        const token = useJwt.getCubusToken()
        const resp = await useJwt.query({ token, query: { method: 'changedomain', param: { domainsid: this.selectedDomainSid } } })
        if (resp.data && resp.data.session) {
          store.commit('cubus-store/SET_SESSION', resp.data.session)
          localStorage.setItem('cubusToken', resp.data.session.token)
          this.addMsg('bot', `Домен: <strong>${resp.data.session.domain.name}</strong>`)
          this.loadRoles()
          this.loadCatalogs()
        }
      } catch (e) {
        this.addMsg('bot', `Ошибка: ${e.message}`)
      }
    },

    async loadRoles() {
      try {
        const qry = await useCubus.loadQuery(this, {
          keymode: 'sid',
          objectsid: 'role',
          pageSize: 100,
        })
        if (qry && qry.recordset && qry.recordset.records) {
          this.availableRoles = qry.recordset.records
            .filter(r => {
              const del = r.del ? r.del.value : false
              const sid = r.sid ? r.sid.value : ''
              return !del && sid !== 'guest' && sid !== 'admin' && sid !== 'dev'
            })
            .map(r => ({
              id: r.id ? r.id.value : 0,
              name: r.name ? r.name.value : '?',
              sid: r.sid ? r.sid.value : '',
            }))
        }
        // Auto-select 'user' role if nothing selected yet
        if (this.defaultRoleSids.length === 0) {
          const userRole = this.availableRoles.find(r => r.sid === 'user')
          if (userRole) this.defaultRoleSids.push(userRole.sid)
        }
      } catch (e) {
        console.error('loadRoles error:', e)
      }
    },

    toggleRoleSid(sid) {
      const idx = this.defaultRoleSids.indexOf(sid)
      if (idx >= 0) this.defaultRoleSids.splice(idx, 1)
      else this.defaultRoleSids.push(sid)
    },

    async loadCatalogs() {
      this.subdivisions = []
      this.positions = []
      try {
        // Find subdivision & position objects by searching metadata
        const objQry = await useCubus.loadQuery(this, { objectsid: 'object', keymode: 'sid', pageSize: 5000 })
        if (!objQry || !objQry.recordset) return
        const objs = objQry.recordset.records.filter(r => !(r.del && r.del.value))
        // Find by name pattern
        this.subObjId = null
        this.posObjId = null
        let subObjId = null
        let posObjId = null
        for (const o of objs) {
          const nm = ((o.name && o.name.value) || '').toLowerCase()
          const sid = ((o.sid && o.sid.value) || '').toLowerCase()
          const id = o.id && o.id.value
          // Подразделение (УО, не МР)
          if (nm === 'подразделение' && sid !== 'refunitmr') { subObjId = id; this.subObjId = id }
          if (!subObjId && (sid === 'business_unit' || sid === 'subdivision')) { subObjId = id; this.subObjId = id }
          if (!posObjId && (nm === 'должность' || sid === 'position')) { posObjId = id; this.posObjId = id }
        }
        const loads = []
        if (subObjId) loads.push(useCubus.loadQuery(this, { objectid: subObjId, keymode: 'sid', pageSize: 10000 }).catch(() => null))
        else loads.push(Promise.resolve(null))
        if (posObjId) loads.push(useCubus.loadQuery(this, { objectid: posObjId, keymode: 'sid', pageSize: 10000 }).catch(() => null))
        else loads.push(Promise.resolve(null))
        const [subQry, posQry] = await Promise.all(loads)
        if (subQry && subQry.recordset && subQry.recordset.records) {
          this.subdivisions = subQry.recordset.records
            .filter(r => !(r.del && r.del.value))
            .map(r => (r.name && r.name.value) || '')
            .filter(n => n)
        }
        if (posQry && posQry.recordset && posQry.recordset.records) {
          this.positions = posQry.recordset.records
            .filter(r => !(r.del && r.del.value))
            .map(r => (r.name && r.name.value) || '')
            .filter(n => n)
        }
      } catch (e) {
        console.error('loadCatalogs error:', e)
      }
    },

    matchSubdivision(val) {
      if (!val) return false
      const v = val.toLowerCase().trim()
      return this.subdivisionNames.some(s => s === v || s.includes(v) || v.includes(s))
    },

    matchPosition(val) {
      if (!val) return false
      const v = val.toLowerCase().trim()
      return this.positionNames.some(s => s === v || s.includes(v) || v.includes(s))
    },

    // ── Ref dropdowns ──
    filteredList(list, query) {
      if (!query || !query.trim()) return list.slice(0, 15)
      const q = query.toLowerCase().trim()
      return list.filter(s => s.toLowerCase().includes(q)).slice(0, 15)
    },
    exactMatch(list, val) {
      if (!val) return false
      const v = val.toLowerCase().trim()
      return list.some(s => s.toLowerCase() === v)
    },
    pickRef(user, field, val) {
      this.$set(user, field, val)
      this.refOpen = null
    },
    async createRef(user, field, val) {
      const objId = field === 'position' ? this.posObjId : this.subObjId
      const catalog = field === 'position' ? 'positions' : 'subdivisions'
      if (!objId) { this.refOpen = null; return }
      try {
        const tpl = await this.apiCall('newrecord', { objectid: objId, keymode: 'sid' })
        const rec = (tpl && tpl.record && tpl.record.record) || (tpl && tpl.record) || null
        if (!rec) throw new Error('пустой шаблон')
        rec.name = { value: val.trim() }
        rec.code = { value: val.trim() }
        rec.del = { value: false }
        await this.apiCall('saverecord', { objectid: objId, keymode: 'sid', record: rec })
        this[catalog].push(val.trim())
        this.$set(user, field, val.trim())
        this.addMsg('bot', `Создано: <strong>${val.trim()}</strong> (${field === 'position' ? 'должнос��ь' : 'подразделение'})`)
      } catch (e) {
        this.addMsg('bot', `Ошибка создания: ${e.message}`)
      }
      this.refOpen = null
    },

    // ── File handling ──
    onFileSelect(e) {
      const file = e.target.files[0]
      if (file) this.processFile(file)
      e.target.value = ''
    },
    onDrop(e) {
      this.dragover = false
      const file = e.dataTransfer.files[0]
      if (file) this.processFile(file)
    },
    async processFile(file) {
      this.addMsg('file', file.name)
      const ext = file.name.split('.').pop().toLowerCase()
      let users = []
      if (ext === 'xlsx' || ext === 'xls') {
        users = await this.parseXlsx(file)
      } else {
        users = await this.parseCsv(file)
      }
      if (users.length === 0) {
        this.addMsg('bot', 'Не удалось распознать пользователей в файле.')
        return
      }
      this.users.push(...users)
      this.addMsg('bot', `Добавлено ${users.length} пользователей в таблицу. Проверьте и нажмите "Зарегистрировать".`)
    },

    async parseXlsx(file) {
      try {
        const data = await file.arrayBuffer()
        const wb = XLSX.read(data, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })
        return this.rowsToUsers(rows.map(r => r.map(c => String(c || '').trim())))
      } catch (e) {
        console.error('XLSX parse error:', e)
        this.addMsg('bot', `Ошибка чтения XLSX: ${e.message}`)
        return []
      }
    },

    parseCsv(file) {
      return new Promise(resolve => {
        const reader = new FileReader()
        reader.onload = e => {
          const text = e.target.result
          const lines = text.split(/\r?\n/).filter(l => l.trim() && !l.startsWith('#'))
          const rows = lines.map(l => l.split(/[;|,\t]/).map(s => s.trim()))
          resolve(this.rowsToUsers(rows))
        }
        reader.readAsText(file, 'UTF-8')
      })
    },

    rowsToUsers(rows) {
      const users = []
      // Try to detect header row and map columns
      const colMap = {
        name: -1, email: -1, subdivision: -1, position: -1,
      }

      // Find header row
      let headerIdx = -1
      for (let ri = 0; ri < Math.min(3, rows.length); ri++) {
        const row = rows[ri].map(c => c.toLowerCase().trim())
        if (row.some(c => c.includes('фио') || c.includes('ф.и.о') || c.includes('имя'))) {
          headerIdx = ri
          row.forEach((c, ci) => {
            if (c.includes('фио') || c.includes('ф.и.о') || c.includes('имя') || c === 'name') colMap.name = ci
            if (c.includes('email') || c.includes('почта') || c.includes('e-mail')) colMap.email = ci
            if (c.includes('подразделение') || c.includes('отдел') || c.includes('department')) colMap.subdivision = ci
            if (c.includes('должность') || c.includes('position')) colMap.position = ci
          })
          break
        }
      }

      const startRow = headerIdx >= 0 ? headerIdx + 1 : 0

      for (let ri = startRow; ri < rows.length; ri++) {
        const row = rows[ri]
        if (!row || row.every(c => !c.trim())) continue

        let email = ''; let name = ''; let subdivision = ''; let
          position = ''

        if (colMap.email >= 0 && colMap.name >= 0) {
          // Use detected column mapping
          email = (row[colMap.email] || '').trim()
          name = (row[colMap.name] || '').trim()
          subdivision = colMap.subdivision >= 0 ? (row[colMap.subdivision] || '').trim() : ''
          position = colMap.position >= 0 ? (row[colMap.position] || '').trim() : ''
        } else {
          // Fallback: find email by @, name by first long text
          const emailIdx = row.findIndex(c => c.includes('@'))
          if (emailIdx < 0) continue
          email = row[emailIdx].trim()
          const nameIdx = row.findIndex((c, i) => i !== emailIdx && c.length > 3 && /[а-яА-ЯёЁa-zA-Z]{2,}/.test(c) && !c.includes('@'))
          name = nameIdx >= 0 ? row[nameIdx] : ''
        }

        if (!email || !email.includes('@') || !name) continue
        const login = email.split('@')[0]
        const password = this.generatePassword()

        users.push({
          name, email, login, password, subdivision, position, status: null, error: '', sid: '',
        })
      }
      return users
    },

    onTextSubmit() {
      const text = this.inputText.trim()
      if (!text) return
      this.inputText = ''
      this.addMsg('user', text)
      const lines = text.split(/\r?\n/).filter(l => l.trim())
      const rows = lines.map(l => l.split(/[;|,\t]/).map(s => s.trim()))
      const users = this.rowsToUsers(rows)
      if (users.length > 0) {
        this.users.push(...users)
        this.addMsg('bot', `Добавлено ${users.length} пользователей в таблицу.`)
      } else {
        this.addMsg('bot', 'Не удалось распознать. Формат: ФИО;email или вставьте таблицу.')
      }
    },

    // ── Registration via registeruser API ──
    async registerAll() {
      if (this.running || this.users.length === 0) return
      this.running = true

      const session = store.getters['cubus-store/SESSION']
      const domainId = session && session.domain && session.domain.id
      const domainName = session && session.domain && session.domain.name
      if (!domainId) {
        this.addMsg('bot', '✗ Сначала переключитесь в нужный домен!')
        this.running = false
        return
      }

      const pending = this.users.filter(u => u.status !== 'ok')
      this.addMsg('bot', `Регистрация ${pending.length} пользователей → <strong>${domainName}</strong>`)

      let success = 0; let fail = 0

      for (let i = 0; i < pending.length; i++) {
        const u = pending[i]
        try {
          const log = []

          // ── Step 1: registeruser → user + name + userdomain + domain copy + roles ──
          let regResult
          try {
            regResult = await this.apiCall('registeruser', {
              name: u.name,
              email: u.email,
              login: u.login,
              password: u.password,
              domain_id: domainId,
              role_sids: this.defaultRoleSids,
              position: u.position || '',
              subdivision: u.subdivision || '',
            })
          } catch (e) {
            throw new Error(`registeruser: ${e.message}`)
          }
          const userSid = regResult && regResult.sid
          if (!userSid) throw new Error(`registeruser: нет SID в ответе: ${JSON.stringify(regResult).substring(0, 100)}`)
          u.sid = userSid

          // Check if user already existed
          if (regResult.status === 'exists') {
            u.status = 'exists'
            u.error = regResult.reason || 'Уже существует'
            fail++
            this.addMsg('bot', `⚠ [${i + 1}/${pending.length}] ${u.name} — ${u.error}`)
            continue
          }

          log.push('user ✓')
          log.push('роли ✓')
          if (regResult.employee) log.push('сотрудник ✓')
          else log.push('сотрудник —')
          if (regResult.position_matched) log.push('должн ✓')
          else if (u.position) log.push(`должн ✗ "${u.position}"`)
          if (regResult.subdivision_matched) log.push('подразд ✓')
          else if (u.subdivision) log.push(`подразд ✗ "${u.subdivision}"`)

          u.posMatched = !!regResult.position_matched
          u.subMatched = !!regResult.subdivision_matched
          u.status = 'ok'
          success++
          this.addMsg('bot', `✓ [${i + 1}/${pending.length}] ${u.name} — ${log.join(', ')}`)
        } catch (e) {
          u.status = 'error'
          u.error = e.message || String(e)
          fail++
          this.addMsg('bot', `✗ [${i + 1}/${pending.length}] ${u.name}: ${u.error}`)
        }
      }

      try { await this.apiCall('clearcache', {}) } catch (e) { /* */ }

      // Validation summary
      const ok = this.users.filter(u => u.status === 'ok')
      const posOk = ok.filter(u => u.posMatched).length
      const subOk = ok.filter(u => u.subMatched).length
      const posMissing = ok.filter(u => u.position && !u.posMatched)
      const subMissing = ok.filter(u => u.subdivision && !u.subMatched)
      let summary = `<strong>Сводка:</strong> ${success} создано, ${fail} ошибок<br>`
      summary += `Должности: ${posOk}/${ok.length} заматчены`
      if (posMissing.length) {
        const missed = [...new Set(posMissing.map(u => u.position))]
        summary += `<br>⚠ Не найдены должности: <em>${missed.join(', ')}</em>`
      }
      summary += `<br>Подразделения: ${subOk}/${ok.length} заматчены`
      if (subMissing.length) {
        const missed = [...new Set(subMissing.map(u => u.subdivision))]
        summary += `<br>⚠ Не найдены подразделения: <em>${missed.join(', ')}</em>`
      }
      this.addMsg('result', summary, null, true, this.users)
      this.running = false
    },

    // ── Downloads ──
    downloadWord(users) {
      let html = '<html><head><meta charset="utf-8"><style>body{font-family:Calibri,sans-serif;font-size:12pt;} .user{margin-bottom:20pt;padding:10pt;border:1pt solid #ddd;page-break-inside:avoid;} .field{margin:2pt 0;} .label{color:#666;font-size:10pt;} .value{font-weight:bold;}</style></head><body>'
      html += '<h2>Данные пользователей</h2>'
      html += `<p>Дата: ${new Date().toLocaleDateString('ru-RU')}</p>`
      html += `<p>Домен: ${this.domainName}</p><hr>`

      users.filter(u => u.status === 'ok').forEach((u, i) => {
        html += '<div class="user">'
        html += `<div class="field"><span class="label">№ </span><span class="value">${i + 1}</span></div>`
        html += `<div class="field"><span class="label">ФИО: </span><span class="value">${u.name}</span></div>`
        html += `<div class="field"><span class="label">Email: </span><span class="value">${u.email}</span></div>`
        html += `<div class="field"><span class="label">Логин: </span><span class="value">${u.login}</span></div>`
        html += `<div class="field"><span class="label">Пароль: </span><span class="value">${u.password}</span></div>`
        if (u.subdivision) html += `<div class="field"><span class="label">Подразделение: </span><span class="value">${u.subdivision}</span></div>`
        html += '</div>'
      })

      html += '</body></html>'
      const blob = new Blob([`\uFEFF${html}`], { type: 'application/msword;charset=utf-8' })
      this.downloadBlob(blob, 'users_credentials.doc')
    },

    downloadCsv(users) {
      const lines = ['ФИО;Email;Логин;Пароль;Подразделение;Статус']
      users.forEach(u => {
        lines.push(`${u.name};${u.email};${u.login};${u.password};${u.subdivision};${u.status || ''}`)
      })
      const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' })
      this.downloadBlob(blob, 'users_credentials.csv')
    },

    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = filename; a.click()
      URL.revokeObjectURL(url)
    },

    // ── Helpers ──
    generatePassword() {
      const u = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; const l = 'abcdefghjkmnpqrstuvwxyz'; const d = '23456789'; const
        s = '!@#$%&*'
      const all = u + l + d + s
      let p = u[Math.floor(Math.random() * u.length)] + l[Math.floor(Math.random() * l.length)] + d[Math.floor(Math.random() * d.length)] + s[Math.floor(Math.random() * s.length)]
      for (let i = 0; i < 8; i++) p += all[Math.floor(Math.random() * all.length)]
      return p.split('').sort(() => Math.random() - 0.5).join('')
    },

    async rawApiCall(token, method, param) {
      const resp = await useJwt.query({ token, query: { method, mode: 'sync', param } })
      const { data } = resp
      if (data.thread) {
        for (let i = 0; i < 30; i++) {
          await new Promise(r => setTimeout(r, 500))
          try {
            const tr = await useJwt.query({ token, query: { method: 'thread', param: { threadname: data.thread } } })
            if (tr.data.thread && tr.data.thread.status === 'done') return tr.data.thread.result
            if (tr.data.thread && tr.data.thread.status === 'error') throw new Error(tr.data.thread.error || 'Thread error')
          } catch (e) { if (i > 5) throw e }
        }
        throw new Error('Timeout')
      }
      if (data.error) throw new Error(data.error)
      return data.result || data
    },

    async apiCall(method, param) {
      const token = useJwt.getCubusToken()
      return this.rawApiCall(token, method, param)
    },

    // BUG-820: в сообщения подставляются имена из импортируемого файла, а выводятся они через v-html.
    safeHtml(text) {
      return DOMPurify.sanitize(String(text || ''))
    },
    addMsg(type, text, users, downloadReady, userData) {
      this.messages.push({
        type, text, users, started: false, downloadReady: downloadReady || false, userData: userData || null,
      })
      this.scrollBottom()
    },

    scrollBottom() {
      this.$nextTick(() => { const el = this.$refs.chatLog; if (el) el.scrollTop = el.scrollHeight })
    },
  },
}
</script>

<style scoped>
.bu-denied { text-align: center; padding: 60px 20px; color: #999; }
.bu-denied h4 { margin: 16px 0 8px; color: #666; }
.bu-page { padding: 20px; max-width: 800px; margin: 0 auto; height: calc(100vh - 60px); display: flex; flex-direction: column; }
.bu-container { flex: 1; display: flex; flex-direction: column; background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden; }
.bu-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid #eee; }
.bu-header h4 { margin: 0; font-size: 16px; }
.bu-domain-select { display: flex; align-items: center; gap: 8px; }
.bu-domain-select label { font-size: 12px; color: #888; margin: 0; }
.bu-domain-picker { padding: 4px 10px; font-size: 13px; border: 1px solid #ddd; border-radius: 4px; font-weight: 600; }

/* Roles bar */
.bu-roles-bar { display: flex; align-items: center; gap: 10px; padding: 6px 16px; border-bottom: 1px solid #eee; background: #fafafa; flex-wrap: wrap; }
.bu-roles-label { font-size: 12px; color: #888; font-weight: 600; white-space: nowrap; }
.bu-role-check { display: flex; align-items: center; gap: 4px; font-size: 12px; cursor: pointer; margin: 0; padding: 2px 8px; border-radius: 12px; background: #f0f0f0; }
.bu-role-check:hover { background: #e8e5ff; }
.bu-role-check input:checked + .bu-role-check { background: #7367f0; color: #fff; }
.bu-role-check input { margin: 0; }
.bu-roles-warn { font-size: 11px; color: #e65100; }
.bu-catalog-info { font-size: 11px; color: #888; }

/* Chat log */
.bu-chat { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 10px; }
.bu-msg { max-width: 90%; padding: 10px 14px; border-radius: 10px; font-size: 13px; line-height: 1.5; }
.bu-msg--bot { background: #f5f5f5; align-self: flex-start; border-bottom-left-radius: 2px; }
.bu-msg--user { background: #7367f0; color: #fff; align-self: flex-end; border-bottom-right-radius: 2px; }
.bu-msg--file { background: #e3f2fd; align-self: flex-end; border-bottom-right-radius: 2px; }
.bu-msg--table { background: #f8f9fa; align-self: flex-start; max-width: 100%; border-bottom-left-radius: 2px; }
.bu-msg--progress { background: #f5f5f5; align-self: flex-start; width: 80%; }
.bu-msg--result { background: #e8f5e9; align-self: flex-start; border-bottom-left-radius: 2px; }
.bu-msg-file { display: flex; align-items: center; gap: 6px; }
.bu-msg-text { margin-bottom: 8px; }
.bu-msg-actions { margin-top: 8px; display: flex; gap: 8px; }
.bu-msg code { background: rgba(0,0,0,0.06); padding: 1px 4px; border-radius: 2px; font-size: 11px; }

/* Preview table */
.bu-preview-table { width: 100%; font-size: 11px; border-collapse: collapse; }
.bu-preview-table th { padding: 4px 8px; background: #eee; font-weight: 600; text-align: left; font-size: 10px; }
.bu-preview-table td { padding: 3px 8px; border-bottom: 1px solid #f0f0f0; }

/* Progress */
.bu-progress-bar { height: 4px; background: #eee; border-radius: 2px; margin-bottom: 6px; }
.bu-progress-fill { height: 100%; background: #7367f0; border-radius: 2px; transition: width 0.3s; }

/* Input area */
.bu-input-area { display: flex; align-items: center; padding: 8px 12px; border-top: 1px solid #eee; gap: 8px; }
.bu-input-area--drag { background: #e8e5ff; border-top-color: #7367f0; }
.bu-file-btn { cursor: pointer; color: #999; padding: 4px; }
.bu-file-btn:hover { color: #7367f0; }
.bu-text-input { flex: 1; padding: 8px 12px; border: 1px solid #eee; border-radius: 20px; font-size: 13px; outline: none; }
.bu-text-input:focus { border-color: #7367f0; }
.bu-send-btn { background: none; border: none; color: #7367f0; cursor: pointer; padding: 4px; }
.bu-send-btn:disabled { color: #ddd; }

/* Table section */
.bu-table-section { border-top: 1px solid #eee; }
.bu-table-header { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; background: #f8f9fa; }
.bu-table-tools { display: flex; gap: 6px; }
.bu-table-wrap { overflow-x: auto; max-height: 300px; overflow-y: auto; }
.bu-table { width: 100%; font-size: 12px; border-collapse: collapse; }
.bu-table th { padding: 5px 8px; background: #f0f0f0; font-size: 10px; font-weight: 600; text-align: left; position: sticky; top: 0; }
.bu-table td { padding: 3px 6px; border-bottom: 1px solid #f5f5f5; }
.bu-num { color: #bbb; text-align: center; font-size: 10px; }
.bu-mono { font-family: monospace; font-size: 11px; color: #555; }
.bu-cell-input { width: 100%; padding: 2px 6px; border: 1px solid transparent; border-radius: 2px; font-size: 12px; outline: none; background: transparent; }
.bu-cell-input:focus { border-color: #7367f0; background: #fff; }
.bu-tr--ok { background: #f0fff0; }
.bu-tr--error { background: #fff5f5; }
.bu-tr--exists { background: #fff8e1; }
.bu-st-exists { color: #e65100; font-size: 10px; }
.bu-st-ok { color: #2e7d32; }
.bu-st-err { color: #c62828; cursor: help; }
.bu-st-wait { color: #ccc; }
.bu-row-del { cursor: pointer; color: #ccc; }
.bu-row-del:hover { color: #e74c3c; }

/* Match highlighting */
.bu-match-ok { background: #e8f5e9 !important; }
.bu-match-ok .bu-cell-input { background: transparent; color: #2e7d32; }
.bu-match-no { background: #fff3e0 !important; }
.bu-match-no .bu-cell-input { background: transparent; color: #e65100; }

/* Ref dropdown */
.bu-ref-cell { position: relative; }
.bu-ref-drop { position: absolute; top: 100%; left: 0; right: 0; z-index: 100; background: #fff; border: 1px solid #ddd; border-radius: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); max-height: 200px; overflow-y: auto; min-width: 180px; }
.bu-ref-opt { padding: 6px 10px; font-size: 12px; cursor: pointer; border-bottom: 1px solid #f5f5f5; }
.bu-ref-opt:hover { background: #e8e5ff; color: #7367f0; }
.bu-ref-opt:last-child { border-bottom: none; }
.bu-ref-create { color: #7367f0; font-weight: 600; background: #f8f6ff; }

</style>
